<script setup>
import IndividualCalls from "@/components/LTE/IndividualCalls.vue";
import GroupCalls from "@/components/LTE/GroupCalls.vue";
import { useGroupCallsStore } from "@/stores/groupCallsStore";
import { useCallsStore } from "@/stores/callsStore";
import { useContactsStore } from "@/stores/contactsStore";
import { useICPStore } from "@/stores/icpStore";
import { onMounted, onBeforeUnmount, ref, watch } from "vue";
import { notifyError } from "@/services/toastService";

const groupCallsStore = useGroupCallsStore();
const callsStore = useCallsStore();
const contactsStore = useContactsStore();

let ICP = useICPStore().ICP;

// pressed state (mouse down or spacebar)
const isTalking = ref(false);

// tracks whether THIS component started a space press (prevents "sticky" states)
const spaceHeld = ref(false);

// ref for the PTT button (optional; we no longer need to force-focus it)
const pttBtnRef = ref(null);

/* =========================
   WATCH PTT DISABLED STATE
   ========================= */

watch(
    () => groupCallsStore.pttDisabled,
    (disabled) => {
      // If PTT becomes disabled while talking, force release immediately
      if (disabled && isTalking.value) {
        isTalking.value = false;
        spaceHeld.value = false;
        try {
          ICP.dispatch.group.pttreleaseTalkingGroup({
            grpid: groupCallsStore.getActiveGroupIsdn,
            callback: function () {}
          });
        } catch (e) {
          console.error("Failed to release PTT when it got disabled", e);
        }
      }
    }
);

/* =========================
   LIFECYCLE
   ========================= */

onMounted(() => {
  contactsStore.selectedHandheld = [];
  contactsStore.selectedTrains = [];
  contactsStore.selectedPhoneContacts = [];

  // Use capture phase so Space doesn't activate focused buttons
  window.addEventListener("keydown", handleKeyDown, true);
  window.addEventListener("keyup", handleKeyUp, true);

  ICP.dispatch.event.register({
    eventType: "GroupCallNotify",
    eventName: "OnTalkingGroupCallPTTFailure",
    callback: function () {
      isTalking.value = false;
      spaceHeld.value = false;
      notifyError("Failed to get the floor in group call. Try again when call returns to 'Idle'.");
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeyDown, true);
  window.removeEventListener("keyup", handleKeyUp, true);
});

/* =========================
   KEYBOARD SUPPORT (SPACE)
   ========================= */

function isTypingInInput(target) {
  if (!target) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

/**
 * IMPORTANT FIX:
 * Always swallow Space (preventDefault/stopPropagation) unless user is typing.
 * Only trigger PTT when it is active (not disabled).
 */
function handleKeyDown(e) {
  if (e.code !== "Space") return;
  if (isTypingInInput(e.target)) return;

  // Always stop Space from "clicking" whichever button is focused
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation?.();

  if (e.repeat) return;
  if (spaceHeld.value) return;

  // If PTT is disabled, do NOTHING — but we've already swallowed Space,
  // so it won't click other buttons.
  if (groupCallsStore.pttDisabled) return;

  spaceHeld.value = true;
  onPttDown();
}

function handleKeyUp(e) {
  if (e.code !== "Space") return;
  if (isTypingInInput(e.target)) return;

  // Always swallow Space on keyup too
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation?.();

  // Only release if we started the press
  if (!spaceHeld.value) return;
  spaceHeld.value = false;

  // Force release even if PTT became disabled between down/up
  onPttUpOrLeave(true);
}

/* =========================
   CALL CONTROL FUNCTIONS
   ========================= */

function hangupCall() {
  if (callsStore.selectedCall.length > 0) {
    if (
        callsStore.selectedCall[0].status === "Ringing" &&
        callsStore.selectedCall[0].direction === "Incoming"
    ) {
      ICP.dispatch.voice.reject({
        cid: callsStore.selectedCall[0].cid,
        callback: () => {
          callsStore.rejectCall(callsStore.selectedCall[0]);
        }
      });
    } else {
      ICP.dispatch.voice.release({
        cid: callsStore.selectedCall[0].cid,
        callback: () => {}
      });
    }
  }
}

function answerCall() {
  if (callsStore.selectedCall.length > 0) {
    ICP.dispatch.voice.answer({
      cid: callsStore.selectedCall[0].cid.toString(),
      callback: () => {}
    });
  }
}

function talkInGroup(isTalkingNow) {
  if (isTalkingNow) {
    if (groupCallsStore.getGroupByIsdn(groupCallsStore.selectedCall[0])?.emergency) {
      ICP.dispatch.group.pttTalkingGroupEmergencyCall({
        grpid: groupCallsStore.getActiveGroupIsdn,
        callback: function () {}
      });
    } else {
      ICP.dispatch.group.pttTalkingGroup({
        grpid: groupCallsStore.getActiveGroupIsdn,
        type: "",
        isEncypt: "",
        source: "",
        fmt: {
          isMute: "",
          video_fmt: "",
          video_type: "",
          audio_type: "",
          amr_mode: ""
        },
        callback: function () {}
      });
    }
  } else {
    ICP.dispatch.group.pttreleaseTalkingGroup({
      grpid: groupCallsStore.getActiveGroupIsdn,
      callback: function () {}
    });
  }
}

/* =========================
   PTT HANDLERS
   ========================= */

function onPttDown() {
  if (groupCallsStore.pttDisabled) return;
  if (isTalking.value) return;

  isTalking.value = true;
  talkInGroup(true);
}

/**
 * force=true is used for keyboard keyup to ensure we always release
 * if we started talking, even if pttDisabled flipped while held.
 */
function onPttUpOrLeave(force = false) {
  if (!force && groupCallsStore.pttDisabled) return;
  if (!isTalking.value) return;

  isTalking.value = false;
  talkInGroup(false);
}

/* =========================
   OTHER FEATURES
   ========================= */

function putInCommunication() {
  const trains = contactsStore.selectedTrains;
  const handheld = contactsStore.selectedHandheld;
  const phoneContacts = contactsStore.selectedPhoneContacts;

  const addList = [];
  if (trains.length === 2 && handheld.length === 0 && phoneContacts.length === 0) {
    addList.push(trains[0].value.isdn);
    addList.push(trains[1].value.isdn);
    groupCallsStore.realignT2TGroup(addList);
  } else if (contactsStore.getAllSelected.length > 1) {
    for (let i = 0; i < contactsStore.selectedTrains.length; i++) {
      addList.push(contactsStore.selectedTrains[i].value.isdn);
    }
    for (let j = 0; j < contactsStore.selectedHandheld.length; j++) {
      addList.push(contactsStore.selectedHandheld[j].value.isdn);
    }
    for (let k = 0; k < contactsStore.selectedPhoneContacts.length; k++) {
      addList.push(contactsStore.selectedPhoneContacts[k].isdn);
    }
    groupCallsStore.realignPatchGroup(addList);
  }
}

function startCall() {
  const isdn = contactsStore.getAllSelected[0].value.isdn;

  ICP.dispatch.voice.dial({
    to: isdn,
    mikey: "",
    callback: () => {}
  });
}

function startEmergencyCall() {
  const isdn = contactsStore.getAllSelected[0].value.isdn;
  useCallsStore().intercepting = { intercept: true, to: isdn };

  ICP.dispatch.voice.intercept({
    to: isdn,
    callback: (rsp) => {
      if (rsp.rsp === "0") {
        useCallsStore().intercepting = { intercept: true, to: isdn };
        console.log("Starting emergency call to " + isdn);
      }
    }
  });
}
</script>

<template>
  <v-col class="d-flex flex-column h-100 pa-0">
    <IndividualCalls />
    <GroupCalls />

    <div id="controlls" class="border-t w-100 d-flex justify-space-evenly align-center">
      <v-btn
          :disabled="contactsStore.getAllSelected.length !== 1"
          class="bg-red text-white"
          @click="startEmergencyCall()"
      >
        Emergency Call
      </v-btn>

      <v-btn
          :class="{ 'bg-green': groupCallsStore.putInCommGreen, 'text-white': groupCallsStore.putInCommGreen }"
          :disabled="contactsStore.getAllSelected.length < 2"
          @click="putInCommunication()"
          class="action-btn"
      >
        Put in communication
      </v-btn>

      <!-- PTT BUTTON -->
      <v-btn
          ref="pttBtnRef"
          :disabled="groupCallsStore.pttDisabled"
          :class="[
          'ptt-btn',
          {
            'ptt-active': !groupCallsStore.pttDisabled,
            'ptt-pressed': isTalking && !groupCallsStore.pttDisabled
          }
        ]"
          icon
          size="large"
          @mousedown="onPttDown"
          @mouseup="onPttUpOrLeave()"
          @mouseleave="onPttUpOrLeave()"
      >
        <div class="d-flex flex-column align-center">
          <v-icon size="34">mdi-microphone</v-icon>
          <span style="font-size: 0.75rem; margin-top: 1px;">PTT</span>
        </div>
      </v-btn>

      <v-btn
          :disabled="callsStore.selectedCall.length === 0"
          @click="hangupCall"
          class="action-btn"
      >
        Hang up
      </v-btn>

      <v-btn
          v-if="contactsStore.selectedPhoneContacts.length === 0 || contactsStore.selectedTrains.length + contactsStore.selectedHandheld !== 1"
          :disabled="callsStore.selectedCall[0]?.status !== 'Ringing' || callsStore.selectedCall[0]?.direction === 'Outgoing'"
          @click="answerCall"
          class="action-btn"
      >
        Answer
      </v-btn>

      <v-btn
          v-if="contactsStore.getAllSelected.length === 1 && useCallsStore().calls.length === 0"
          @click="startCall()"
          class="action-btn"
      >
        Call
      </v-btn>
    </div>
  </v-col>
</template>

<style scoped>
/* Action buttons: bluish when enabled, white/gray when disabled */
.action-btn {
  background-color: #1d7fe6;
  color: white;
  min-width: 110px;
  padding: 8px 14px;
  border-radius: 6px;
  transition: filter 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease;
  box-shadow: 0 2px 6px rgba(29, 126, 230, 0.12);
}

/* Hover/active only when not disabled */
.action-btn:not([disabled]):hover {
  filter: brightness(1.06);
  box-shadow: 0 6px 16px rgba(29, 126, 230, 0.18);
}

/* Disabled appearance: light/gray */
.action-btn[disabled],
.action-btn[aria-disabled="true"] {
  background-color: #f5f5f5;
  color: #9e9e9e;
  box-shadow: none;
  cursor: default;
}

#controlls {
  height: 70px;
  background-color: darkgray;
}

.ptt-btn {
  position: relative;
  border-radius: 50% !important;
  width: 55px;
  height: 55px;
  margin-top: 6px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.08s ease, filter 0.2s ease, opacity 0.2s ease;
}

.ptt-btn:deep(.v-btn__content) {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.ptt-active,
.ptt-pressed {
  color: white;
  box-shadow:
      0 0 8px rgba(255, 255, 255, 0.15),
      0 0 18px rgba(0, 0, 0, 0.15),
      inset 0 2px 6px rgba(255, 255, 255, 0.6),
      inset 0 -3px 6px rgba(0, 0, 0, 0.4);
}

.ptt-active {
  background: radial-gradient(circle at 30% 30%, #7ad1ff, #1d7fe6 60%, #0b49a8 100%);
  color: white;
  box-shadow:
      0 0 10px rgba(30, 144, 255, 0.85),
      0 0 26px rgba(30, 144, 255, 0.55),
      inset 0 2px 6px rgba(255, 255, 255, 0.6),
      inset 0 -3px 6px rgba(0, 0, 0, 0.4);
  animation: vistaPulseBlue 1.4s infinite ease-in-out;
}

.ptt-pressed {
  background: radial-gradient(circle at 30% 30%, #8cffb0, #22c55e 60%, #0b7a2d 100%);
  color: white;
  box-shadow:
      0 0 10px rgba(34, 197, 94, 0.9),
      0 0 28px rgba(34, 197, 94, 0.6),
      inset 0 2px 6px rgba(255, 255, 255, 0.6),
      inset 0 -3px 6px rgba(0, 0, 0, 0.45);
  animation: none;
  transform: translateY(1px);
  filter: saturate(1.05);
}

.ptt-active::before,
.ptt-pressed::before {
  content: "";
  position: absolute;
  top: 6%;
  left: 10%;
  width: 80%;
  height: 45%;
  background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.85),
      rgba(255, 255, 255, 0.25),
      transparent
  );
  border-radius: 50%;
  pointer-events: none;
}

@keyframes vistaPulseBlue {
  0% {
    box-shadow:
        0 0 10px rgba(30, 144, 255, 0.85),
        0 0 26px rgba(30, 144, 255, 0.55),
        inset 0 2px 6px rgba(255, 255, 255, 0.6),
        inset 0 -3px 6px rgba(0, 0, 0, 0.4);
  }
  50% {
    box-shadow:
        0 0 14px rgba(30, 144, 255, 1),
        0 0 34px rgba(30, 144, 255, 0.75),
        inset 0 2px 6px rgba(255, 255, 255, 0.6),
        inset 0 -3px 6px rgba(0, 0, 0, 0.4);
  }
  100% {
    box-shadow:
        0 0 10px rgba(30, 144, 255, 0.85),
        0 0 26px rgba(30, 144, 255, 0.55),
        inset 0 2px 6px rgba(255, 255, 255, 0.6),
        inset 0 -3px 6px rgba(0, 0, 0, 0.4);
  }
}
</style>
